import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Button, CustomIcon } from '@components/shared';
import { useBreakpoint } from '@shared/useBreakpoints';

import Layout from '../Layout';
import EmptyTourbooksStateIllustration from './EmptyTourbooksStateIllustration';

type Props = {
  onClickCreateTourbook: () => void;
};

const EmptyState = ({ onClickCreateTourbook }: Props) => {
  const { t } = useTranslation('tourbook');
  const { isDesktop } = useBreakpoint();

  // clipPath not supported by tailwind at this time
  const desktopBackgroundCustomColorStyle = {
    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 20% 100%)',
  };
  const mobileTabletBackgroundCustomColorStyle = {
    clipPath: 'polygon(0% 45%, 100% 0%, 100% 100%, 0% 100%)',
  };

  return (
    <Layout
      containerClassName={cn(
        'flex justify-center',
        'mobile:!p-2',
        'tablet:!px-2 tablet:!py-7',
        'desktop:!py-[120px]',
      )}
    >
      <div
        className={cn(
          'relative flex max-w-[1183px] shadow-[0_1px_10px_0_rgb(0,0,0,0.25)]',
          'mobile:flex-col',
          'tablet:flex-col',
        )}
      >
        <div
          className={cn(
            'pr-2, w-2/5 py-12 pl-6',
            'tablet:w-full tablet:px-4 tablet:pb-8 tablet:pt-7',
            'mobile:w-full mobile:px-2 mobile:pb-4 mobile:pt-3',
          )}
        >
          <div className="mb-2 flex items-center gap-1">
            <span className="font-headline">{t('indexEmptyState.tourbooks')}</span>
            <CustomIcon type="tourbook" className="text-indigo-100 [&>svg]:h-3 [&>svg]:w-3" />
          </div>
          <div className="mb-2 font-body-large-emphasis">{t('indexEmptyState.description')}</div>
          <ul className="mb-4 ml-2 flex flex-col gap-2">
            <li className="list-disc text-indigo-100">
              <span className="text-black-100 font-body-large">
                {t('indexEmptyState.addListings')}
              </span>
            </li>
            <li className="list-disc text-indigo-100">
              <span className="text-black-100 font-body-large">
                {t('indexEmptyState.customize')}
              </span>
            </li>
            <li className="list-disc text-indigo-100">
              <span className="text-black-100 font-body-large">
                {t('indexEmptyState.valueProp')}
              </span>
            </li>
          </ul>

          <Button onClick={() => onClickCreateTourbook()} size="large" data-testid="createTourbook">
            {t('createTourbook')}
          </Button>
        </div>

        <div
          className={cn(
            'absolute right-0 z-[-1] h-full w-[58%] bg-aqua-20',
            'mobile:bottom-0 mobile:h-[58%] mobile:w-full',
            'tablet:bottom-0 tablet:h-[58%] tablet:w-full',
          )}
          style={
            isDesktop ? desktopBackgroundCustomColorStyle : mobileTabletBackgroundCustomColorStyle
          }
        ></div>
        <div className="flex items-center desktop:pr-2 tablet:pl-1 mobile:-ml-[4px]">
          <EmptyTourbooksStateIllustration />
        </div>
      </div>
    </Layout>
  );
};

export default EmptyState;
